import { Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import { NEW_APPOINTMENT_SCHEMA } from "../../../../../constants/scheduling";
import { useAddAppointment } from "../../../../../queries/use-add-appointment";
// import { useSpecialtiesList } from "../../../../../queries/use-specialities-list";
import { Elements } from "@stripe/react-stripe-js";
import PaymentDrawer from "../../../../../components/app/provider/billing/payment/PaymentDrawer";
import useCurrentUser from "../../../../../hooks/local-storage/use-current-user";
import useSelectedPatient from "../../../../../hooks/local-storage/use-selected-patient";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { getStripePromise } from "../../../../../queries/stripe/use-stripe";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../utils/utils";
import {
  NEW_APPOINTMENT_INITIAL_VALUES,
  NewAppointmentType,
} from "./appointment-constant";
import { NewAppointmentForm } from "./appointment-form/NewAppointmentForm";
interface NewAppointmentProps {
  opened: boolean;
  onClose: () => void;
  hideRecurring?: boolean;
  enablePrefillPatientData?: boolean;
  prefillPatientData?: {
    value: string;
    label: string;
    birthDate: string;
  };
}

const NewAppointment = ({
  opened,
  onClose,
  hideRecurring,
  enablePrefillPatientData = false,
  prefillPatientData,
}: NewAppointmentProps) => {
  const [me] = useCurrentUser();
  const showSnackbar = useSnackbar();
  const client = useQueryClient();

  const { mutateAsync: addAppointment } = useAddAppointment();
  const payDisc = useDisclosure();
  const [selectedPatient] = useSelectedPatient();

  const prefillPatientField = {
    value: selectedPatient?.uuid ?? "",
    label: `${selectedPatient?.firstName ?? ""} ${
      selectedPatient?.lastName ?? ""
    }`,
    birthDate: selectedPatient?.birthDate ?? "",
  };

  const meData = {
    birthDate: me?.birthDate,
    colorCode: me?.colorCode,
    label: `${me?.firstName || ""} ${me?.lastName || ""}`,
    value: me?.uuid,
  };
  const formik = useFormik<NewAppointmentType>({
    initialValues: {
      ...NEW_APPOINTMENT_INITIAL_VALUES,
      providerId: meData as any,
      patientId: enablePrefillPatientData
        ? prefillPatientField
        : prefillPatientData
          ? prefillPatientData
          : undefined,
    },
    validationSchema: NEW_APPOINTMENT_SCHEMA,
    enableReinitialize: true,
    onSubmit: SubmitHandler,
  });

  function SubmitHandler(values: any) {
    // const selectedSpecialty = specialtiesList?.data?.content?.find(
    //   (it) => it?.uuid === values?.providerSpecialities
    // );

    const timezone = values?.timezone?.curType;
    const customForms = values?.customForms
      ? values?.customForms?.map((it: any) => it?.value)?.join(",")
      : null;
    const patientId = values?.patientId?.value;
    const assessments = values?.assessments
      ?.map((it: any) => it?.value)
      ?.join(",");
    // Extract UUID from type field (if it's an object, get the value, otherwise use the string)
    const appointmentTypeLabel =
      typeof values?.type === "object" && values?.type?.label
        ? values.type.label
        : values?.type;

    // const providerSpecialities = selectedSpecialty
    //   ? { [selectedSpecialty.uuid]: selectedSpecialty.name }
    //   : null;

    const payload = {
      ...values,
      type: appointmentTypeLabel, // Send only UUID
      appointmentTypeId: values?.type?.value,
      customForms: customForms,
      primary_insurance: undefined,
      secondary_insurance: undefined,
      patientPrimaryInsurance: values?.primary_insurance?.uuid
        ? values?.primary_insurance
        : undefined,
      patientSecondaryInsurance: values?.secondary_insurance?.uuid
        ? values?.secondary_insurance
        : undefined,
      timezone,
      patientId,
      assessments,
      locationId: values?.locationId?.value,
      providerId: values.providerId.value!,
      reminders: values.reminder_set ? values?.reminders : undefined,
      // providerSpecialities,
      providerName: me?.firstName + " " + me?.lastName,
      providerRole: me?.role,
      xTENANTID: getXtenantId(),
    };

    addAppointment(payload)
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        client.invalidateQueries({ queryKey: ["appointment-list"] });
        client.invalidateQueries({ queryKey: ["patient-chart-forms-list"] });
        client.invalidateQueries({ queryKey: ["appointment-list-v2"] });
        client.invalidateQueries({ queryKey: ["future-appointment"] });

        closetAndReset();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  const closetAndReset = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <SideSheetDrawer
      open={opened}
      title="Schedule Appointment"
      onClose={closetAndReset}
      w="42vw"
      footerActions={
        <>
          {/* ECRH-4991: Collect Payment removed from Schedule Appointment footer.
          <Button
            size="medium"
            variant="outlined"
            disabled={!formik.values.patientId}
            onClick={() =>
              payDisc.open({
                uuid: null,
                patientId: formik.values.patientId?.value,
              })
            }
            sx={{ lineHeight: 1.5 }}
          >
            Collect Payment
          </Button>
          */}
          <Button
            size="medium"
            variant="contained"
            onClick={() => formik.handleSubmit()}
            sx={{ lineHeight: 1.5 }}
          >
            Save And Close
          </Button>
        </>
      }
    >
      {payDisc.opened && (
        <Elements stripe={getStripePromise()}>
          <PaymentDrawer
            disclosureHost={payDisc}
            amount={payDisc?.data?.amount}
            onSuccess={() => {
              // closetAndReset();
            }}
            shouldPassPatientId={true}
            providerId={
              formik.values.providerId?.value
                ? {
                    uuid: formik.values.providerId.value,
                    name: formik.values.providerId.label ?? "",
                  }
                : undefined
            }
          />
        </Elements>
      )}
      <NewAppointmentForm
        formik={formik}
        hideRecurring={hideRecurring}
        disabledPatientField={!!prefillPatientField && enablePrefillPatientData}
      />
    </SideSheetDrawer>
  );
};

export default NewAppointment;
